import { useState, useEffect } from "react";
import { FormModal, Field, Input, Textarea, Select } from "./form";
import DeleteRecordButton from "./DeleteRecordButton";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { PROPERTY_TYPES, PROPERTY_STATUSES } from "../lib/constants";

export default function PropertyForm({ open, onClose, property, onSaved }) {
  const nav = useNavigate();
  const empty = {
    address: "", street: "", city: "", county: "", state: "ID", zip: "", property_type: "single_family",
    status: "active", listing_source: "", mls_number: "", source_url: "", asking_price: "", current_price: "",
    original_price: "", beds: "", baths: "", square_feet: "", lot_size: "", acreage: "", year_built: "",
    garage: "", days_on_market: "", hoa: "", property_taxes: "", tags: [], description: "",
  };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setForm(property ? { ...empty, ...property } : empty); }, [open, property]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (!payload.current_price) payload.current_price = payload.asking_price;
      if (property) await api.put(`/properties/${property.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(property ? "Property updated" : "Property saved");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  };

  const tagsStr = Array.isArray(form.tags) ? form.tags.join(", ") : form.tags;

  return (
    <FormModal open={open} onClose={onClose} title={property ? "Edit Property" : "New Property"} onSubmit={submit} loading={loading} size="max-w-2xl" testid="property-form-modal"
      footerLeft={property?.id && <DeleteRecordButton entity="properties" id={property.id} label="Property" onDeleted={() => { onClose(); nav("/properties"); }} />}>
      <Field label="Address" required><Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="174 Alturas St" required data-testid="property-address" /></Field>
      <div className="grid grid-cols-4 gap-3">
        <Field label="City" className="col-span-2"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
        <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
        <Field label="ZIP"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
      </div>
      {property?.id && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Map pin latitude (override)"><Input type="number" step="any" value={form.latitude ?? ""} onChange={(e) => set("latitude", e.target.value === "" ? null : Number(e.target.value))} data-testid="property-lat" /></Field>
          <Field label="Map pin longitude (override)"><Input type="number" step="any" value={form.longitude ?? ""} onChange={(e) => set("longitude", e.target.value === "" ? null : Number(e.target.value))} data-testid="property-lng" /></Field>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <Field label="Property type"><Select options={PROPERTY_TYPES} value={form.property_type} onChange={(v) => set("property_type", v)} testid="property-type" /></Field>
        <Field label="Status"><Select options={PROPERTY_STATUSES} value={form.status} onChange={(v) => set("status", v)} testid="property-status" /></Field>
        <Field label="MLS number"><Input value={form.mls_number} onChange={(e) => set("mls_number", e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Asking price"><Input type="number" value={form.asking_price} onChange={(e) => set("asking_price", Number(e.target.value))} data-testid="property-price" /></Field>
        <Field label="Original price"><Input type="number" value={form.original_price} onChange={(e) => set("original_price", Number(e.target.value))} /></Field>
        <Field label="Days on market"><Input type="number" value={form.days_on_market} onChange={(e) => set("days_on_market", Number(e.target.value))} /></Field>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Field label="Beds"><Input type="number" value={form.beds} onChange={(e) => set("beds", Number(e.target.value))} /></Field>
        <Field label="Baths"><Input type="number" value={form.baths} onChange={(e) => set("baths", Number(e.target.value))} /></Field>
        <Field label="Sq Ft"><Input type="number" value={form.square_feet} onChange={(e) => set("square_feet", Number(e.target.value))} /></Field>
        <Field label="Year built"><Input type="number" value={form.year_built} onChange={(e) => set("year_built", Number(e.target.value))} /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Garage"><Input value={form.garage} onChange={(e) => set("garage", e.target.value)} /></Field>
        <Field label="Acreage"><Input type="number" value={form.acreage} onChange={(e) => set("acreage", Number(e.target.value))} /></Field>
        <Field label="Listing source"><Input value={form.listing_source} onChange={(e) => set("listing_source", e.target.value)} /></Field>
      </div>
      <Field label="Tags (comma separated)"><Input value={tagsStr} onChange={(e) => set("tags", e.target.value)} placeholder="Luxury, Price Reduction" /></Field>
      <Field label="Description / Notes"><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
    </FormModal>
  );
}
